/**
 * This is a helper which will keep you calling API DRY:
 *
 * call API (via api.ts) at high-level:
 *   1. if argument has mistake (ex: blink an inexisting device), diaplay error message (with i18n) in snackbar.
 *   2. if success, display snackbar & call needed mutations
 *   3. If has error in returned value:
 *     a. If the reason is client side network, show a dialog on screen.
 *     b. If the reason is other reason, display {@link $t(msg.diaplay)}: correcponding
 *
 * - Each function ALWAYS MUST returns a Promise<boolean> to indicate if finished succes/failed? (e.g. can be used by recall/fail retry)
 * User should NEVER need to do something else after calling easyapi.
 *
 * TODO: Failure retry, wrap function along with arguments in closure
 */
import { api } from '@/singleton'
import { $s } from '@/store/index'
import { getDevRef } from '@/store/modules/device'
import { IDeviceAss, dev_identity_t, IDevice, IVW } from '@/types/model'
import { LocaleMessage } from 'vue-i18n'
import i18n from '@/i18n'
import { ppp, SORT_BY_ID_FN, deepCopy, generateUnoccupiedId } from '@/utils'
import { SafeResponse, SafePromise } from '@/request_manager'
import { ApiParamExtenderEDID, DeviceType, ApiVw, OsdConfig, SystemNetwork, OsdImage, ApiUploadOsdImage, Preset } from '@/types/api'
import Snackbar from 'node-snackbar'
import { vwValidateRxTxList } from '@/vw_algorithm'

type msg_text_t = string | LocaleMessage
interface Vm {
  $s: typeof $s
}


/** If success, return true. NEVER USE Promise.reject() */
type EasyApiReturn = Promise<boolean>

function isNetworkError <T> (res: SafeResponse<T>): boolean {
  return (!res.ok && res.err.code === -1)
}
type msg_category_t = 'default' | 'success' | 'error' | 'warning' | 'info'

function getSnackbarOptions (category: msg_category_t): Partial<SnackbarOptions> {
  switch (category) {
    case 'default': return { }
    case 'success': return { backgroundColor: '#86c255' }
    case 'info': return { backgroundColor: '#20b8ff' }
    case 'error': return { backgroundColor: '#ff4444' }
    case 'warning': return { backgroundColor: '#ffaa00'}
    default: return { }
  }
}

export function showSnackbar (msg: msg_text_t, category: msg_category_t = 'default'): void {
  Snackbar.show(Object.assign({
    text: msg as string,
    pos: 'bottom-center',
    showAction: false,
  }, getSnackbarOptions(category)))
}

/** Show dialog if SafeResponse indicate a common error.
 * Currently only NetworkError can be handled by this function.
 */
function handleCommonError <T> (resFromApi: SafeResponse<T>): void {
  if (resFromApi.ok) {
    if ($s.$state.common.networkError) { $s.common.mutations.assignNetworkError(false) }
    return
  } else {
    if (resFromApi.err.code === -1) {
      if (!$s.$state.common.networkError) { $s.common.mutations.assignNetworkError(true) }
    }
  }
}


// tslint:disable-next-line:class-name
export default class easyapi {
  static blinkLed (vm: Vm, x: dev_identity_t): EasyApiReturn {
    console.log('[Easy API] invoked!')
    const dev = getDevRef(x)
    if (!dev) { throw new Error('[DEV] this should no happened') }
    if (dev.ledBlinking) {
      showSnackbar(i18n.t('maintenance.blinkLed.hasBeingBlinking'))
      return Promise.resolve(true)
    }
    const mac = dev.mac
    // In fact, Extender doesn't support cancelling the blinking, so set value to false is same with to true.
    return api.setLedBlink({ mac, value: true }).then((r) => {
      handleCommonError(r)
      if (!r.ok) {
        showSnackbar(i18n.t('msg.applyFailed') + r.err.msg)
        return false
      }
      showSnackbar(i18n.t('maintenance.blinkLed.blinkedSuccessfully'))
      setTimeout(() => {
        $s.device.mutations.assignDev({ asstar: dev, ledBlinking: false })  // NOTE: call $s via vm, don't call $s directly, it will exploded. (really?)
      }, 28 * 1000)
      $s.device.mutations.assignDev({ asstar: dev, ledBlinking: true })
      return true
    })
  }
  static renameDevice (vm: Vm, device: dev_identity_t, newName: string): EasyApiReturn {
    const dev = getDevRef(device)
    if (!dev) { throw new Error('[To Developer] This should not happened.') }
    return api.setDeviceName({ mac: dev.mac, value: newName }).then((r) => {
      handleCommonError(r)
      if (r.ok) {
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static rebootDevice (device: dev_identity_t): EasyApiReturn {
    const dev = getDevRef(device)
    if (!dev) { throw new Error('[To Developer] This should not happened.') }
    return api.rebootDevice({ mac: dev.mac }).then((r) => {
      handleCommonError(r)
      if (r.ok) {
        showSnackbar(i18n.t('msg.applyDone'))
        // $s.device.mutations.assignDev({ asstar: dev  })  // TODO: Is this needed?
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static resetDevice (device: dev_identity_t): EasyApiReturn {
    const dev = getDevRef(device)
    if (!dev) { throw new Error('[To Developer] This should not happened.') }
    return api.resetDevice({ mac: dev.mac }).then((r) => {
      handleCommonError(r)
      if (r.ok) {
        showSnackbar(i18n.t('msg.applyDone'))
        // $s.device.mutations.assignDev({ asstar: dev })  // TODO: Is this needed?
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static toggleCEC (vm: Vm, x: dev_identity_t): EasyApiReturn {  // FIXME: Wait for New API
    const dev = getDevRef(x)
    if (!dev) { throw new Error('[To Developer] This should not happened.') }
    const newCecFlag = !dev.cecFlag
    return api.setExtenderCEC({ mac: dev.mac, value: newCecFlag }).then((r) => {
      handleCommonError(r)
      if (!r.ok) {
        showSnackbar(i18n.t('msg.applyFailed') + `(${r.err.msg})`)
        return false
      } else {
        showSnackbar(i18n.t('msg.applyDone'))
        $s.device.mutations.assignDev({ asstar: dev, cecFlag: newCecFlag })
        return true
      }
    })
  }
  /** Disable video + audio */
  static toggleAvOutput (vm: Vm, x: dev_identity_t): EasyApiReturn {
    const dev = getDevRef(x)
    if (!dev) { throw new Error('[To Developer] This should not happened.') }
    if (dev.vendor === 'Aptovision' && dev.deviceType === 'RX') {
      console.error('[To Developer] 10G RX does not support toggleAvOutput()')
      return Promise.resolve(false)
    }
    const newVal = !dev.disableAVOutput
    return api.setDisableAvOutput({mac: dev.mac, value: newVal}).then((r) => {
      handleCommonError(r)
      if (!r.ok) {
        showSnackbar(i18n.t('msg.applyFailed') + `(${r.err.msg})`)
        return false
      } else {
        showSnackbar(i18n.t('msg.applyDone'))
        $s.device.mutations.assignDev({ asstar: dev, disableAVOutput: newVal })
        return true
      }
    })
  }
  static async toggleLoopOut (vm: Vm, x: dev_identity_t): EasyApiReturn {   // FIXME: Wait for New API
    const dev = getDevRef(x)
    if (!dev) { throw new Error('[To Developer] This should not happened.') }
    showSnackbar('[To Developer] Not Implemented.')
    return false
  }
  static setEdid (vm: Vm, param: ApiParamExtenderEDID): EasyApiReturn {
    const dev = getDevRef(param.mac)
    if (!dev) { throw new Error('[To Developer] This should not happened.') }
    return api.setExtenderEDIDCommand(param).then((r) => {
      handleCommonError(r)
      if (r.ok) {
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed') + `(${r.err.msg})`)
        return false
      }
    })
  }
  /** Set any amounts of TX/RX with the same GroupID. */
  static async setMatrix (txrx: IDevice[]): EasyApiReturn {
    console.log('setMatrix', txrx)
    if (txrx.length === 0) {
      console.error('[To Developer] This should not be happened. A Matrix group should always contains >= 2 devices...')
      return false
    }
    const types: DeviceType[] = txrx.map((x) => x.deviceType)
    if (!types.includes('RX')) {
      console.error('[To Developer] This should not be happened. For join Matrix, at least 1 RX is required.')
      return false
    }
    if (types.filter(x => x === 'TX').length > 1) {
      console.error('[To Developer] This should not be happened. A Matrix group should always contains only 1 TX...')
      return false
    }
    const tx =  txrx.find(x => x.deviceType === 'TX')
    let txMac: string
    if (tx) {
      txMac = tx.mac
    } else {
      txMac = ''  // empty TX mac means set the groupId of RX into an unoccupied groupId.
    }
    for (const dev of txrx) {
      if (dev.deviceType === 'TX') { continue }
      // TODO: Handle RDBMS-liked transaction when failed?
      const res = await api.matrixJoin({ txMac: txMac, rxMac: dev.mac })
      handleCommonError(res)
      if (!res.ok) {
        showSnackbar(i18n.t('msg.applyFailed') + `(${res.err.msg})`)
        return false
      }
    }
    showSnackbar(i18n.t('msg.applyDone'))
    return true
  }
  static fetchAllFirstTimeData (): EasyApiReturn {
    return Promise.all([
      api.getExtenderList(),
      api.getVwList(),
      api.getOsdList(),
      api.getOsdImageList(),
      api.getPresetList(),
      api.getSystemBasicInfo(),
      api.getSystemNetworkSetting(),
      api.getSystemNetworkStatus()
    ]).then(([ext, vw, osd, osdimg, preset, basicinfo, netset, netstat]) => {
      if (ext.ok) { $s.device.mutations.assignList(ext.d) }
      if (vw.ok) { $s.device.mutations.assignVwList(vw.d) }
      if (osd.ok) { $s.device.mutations.assignOsdList(osd.d) }
      if (osdimg.ok) { $s.device.mutations.assignOsdImgList(osdimg.d) }
      if (preset.ok) { $s.device.mutations.assignPresetList(preset.d) }
      if (basicinfo.ok) { $s.sys.mutations.assignBasicInfo(basicinfo.d) }
      if (netset.ok) { $s.sys.mutations.assignNetworkSetting(netset.d) }
      if (netstat.ok) { $s.sys.mutations.assignNetworkStatus(netstat.d) }
      if (!$s.$state.device.firstTimeFetched) { $s.device.mutations._firstTimeFetched(true) }
      return true
    })
  }
  static getAllVw (): EasyApiReturn {
    return api.getVwList().then((r) => {
      handleCommonError(r)
      if (r.ok) {
        $s.device.mutations.assignVwList(r.d)
        return true
      } else {
        console.error('[To Developer][EasyAPI] Error occurs when fetching VideoWall list')
        return false
      }
    })
  }
  static createVw (vw: ApiVw): EasyApiReturn {
    vw.id = generateUnoccupiedId($s.$state.device.vwList)
    vwValidateRxTxList(vw)
    return api.setOrCreateVw(vw).then(res => {
      handleCommonError(res)
      if (res.ok) {
        $s.device.mutations.addVw(vw)
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static modifyVw (vw: ApiVw): EasyApiReturn {
    const vwRef = $s.$state.device.vwList.find(v => v.id === vw.id)
    if (!vwRef) { throw new Error('[To Developer] This should not be happened.') }
    vw = vwValidateRxTxList(vw)
    return api.setOrCreateVw(vw).then(r => {
      handleCommonError(r)
      if (r.ok) {
        $s.device.mutations.assignVw({vwid: vw.id, value: vw})
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static renameVw (vwId: number, newName: string): EasyApiReturn {
    const vw = $s.$state.device.vwList.find(v => v.id === vwId)
    if (!vw) { throw new Error('[To Developer] This should not be happened.') }
    const newObj: ApiVw = Object.assign({}, vw, { name: newName })
    return api.setOrCreateVw(newObj).then(r => {
      handleCommonError(r)
      if (r.ok) {
        $s.device.mutations.assignVw({vwid: vw.id, value: newObj})
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static deleteVw (vwId: number): EasyApiReturn {
    const vw = $s.$state.device.vwList.find(v => v.id === vwId)
    if (!vw) { throw new Error('[To Developer] This should not be happened.') }
    return api.vwDelete({id: vwId}).then(r => {
      handleCommonError(r)
      if (r.ok) {
        $s.device.mutations.deleteVw(vwId)
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static applyVw (vwId: number): EasyApiReturn {
    const vw = $s.$state.device.vwList.find(v => v.id === vwId)
    if (!vw) { throw new Error('[To Developer] This should not be happened.') }
    return api.vwApply({id: vwId}).then(r => {
      handleCommonError(r)
      if (r.ok) {
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static toggleVWActive (vwId: number): EasyApiReturn { // FIXME: Wait For API
    const vw = $s.$state.device.vwList.find(v => v.id === vwId)
    if (!vw) { throw new Error('[To Developer] This should not be happened.') }
    const newActive = !vw.enabled
    const newVw: ApiVw = Object.assign(vw, { active: newActive })
    showSnackbar('[To Developer] Wait for new API', 'error')
    return Promise.resolve(false)
  }
  static toggleVWOSD (vwId: number): EasyApiReturn { // FIXME: Wait for API
    const vw = $s.$state.device.vwList.find(v => v.id === vwId)
    if (!vw) { throw new Error('[To Developer] This should not be happened.') }
    showSnackbar('[To Developer] Wait for new API', 'error')
    return Promise.resolve(false)
  }
  static vwPreviewStart (vw: ApiVw): EasyApiReturn {
    return api.vwPreviewStart(vw).then((r) => {
      return r.ok
    })
  }
  static vwPreviewRestore (): EasyApiReturn {
    return api.vwPreviewRestore().then((r) => {
      return r.ok
    })
  }
  static fetchSystemBasicInfo (): EasyApiReturn {
    return api.getSystemBasicInfo().then((res) => {
      handleCommonError(res)
      if (!res.ok) { return false }
      $s.sys.mutations.assignBasicInfo(res.d)
      console.log('sys info ==> ', res.d)
      return true
    })
  }
  static fetchNetworkStatus (): EasyApiReturn {
    return api.getSystemNetworkStatus().then((res) => {
      handleCommonError(res)
      if (!res.ok) { return false }
      $s.sys.mutations.assignNetworkStatus(res.d)
      $s.sys.mutations.assignNetworkSetting(res.d)
      console.log('sys info ==> ', res.d)
      return true
    })
  }

  static setNetworkConfig (param: SystemNetwork): EasyApiReturn {
    return api.setSystemNetworkSetting(param).then((res) => {
      handleCommonError(res)
      if (!res.ok) { return false }
      // $s.sys.mutations.assignBasicInfo(res.d)
      // WIP lots to do, maybe redirect to new ip?
      console.log('set network ==> ', res.d)
      return true
    })
  }

  static loadCurrentUserPreference (): EasyApiReturn {
    return api.getCurrentUserPreference().then((res: null|object) => {
      console.log('preference', res)
      $s.pref.mutations.assignStateSafely(res)
      return true
    })
  }
  static saveCurrentUserPreference (): EasyApiReturn {
    return api.setCurrentUserPreference($s.$state.pref).then((r) => {
      if (r) {
        // showSnackbar('Preference saved')
        return true
      } else {
        showSnackbar('Failed when saving preference')
        return false
      }
    })
  }
  static vwApplyDisplayIndex (vwId: number, value: boolean): EasyApiReturn {
    return api.vwApplyRxIndexOsd({id: vwId, value: value}).then((r) => {
      handleCommonError(r)
      if (r) {
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static getOsdAndImageList (): EasyApiReturn {
    return Promise.all([
      api.getOsdList(),
      api.getOsdImageList(),
    ]).then(([osdList, imgList]) => {
      handleCommonError(osdList)
      if (osdList.ok && imgList.ok) {
        $s.device.mutations.assignOsdImgList(imgList.d)
        $s.device.mutations.assignOsdList(osdList.d)
        return true
      }
      return false
    })
  }
  static createOsd (osd: OsdConfig): EasyApiReturn {
    const newId = generateUnoccupiedId($s.$state.device.osd.list)
    osd.id = newId
    return api.createOrUpdateOsd(osd).then((r) => {
      if (r.ok) {
        $s.device.mutations.assignOrCreateOsd(osd)
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static modifyOsd (osd: OsdConfig): EasyApiReturn {
    return api.createOrUpdateOsd(osd).then((r) => {
      if (r.ok) {
        showSnackbar(i18n.t('msg.applyDone'))
        $s.device.mutations.assignOrCreateOsd(osd)
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static deleteOsdImg (img: OsdImage): EasyApiReturn {
    return api.deleteOsdImage({ id: img.id }).then((r) => {
      if (r.ok) {
        showSnackbar(i18n.t('msg.applyDone'))
        $s.device.mutations.deleteOsdImg(img)
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static uploadImageFile (imageFile: Blob, overwriteToId: number = -1): EasyApiReturn {
    const id = overwriteToId !== -1 ?  overwriteToId : generateUnoccupiedId($s.$state.device.osd.imgList)
    const tmp: ApiUploadOsdImage = {
      id: id,
      image: imageFile
    }
    return api.uploadOsdImage(tmp)
  }
  static createOrModifyImageConfig (imgCfg: OsdImage, createOrModify: 'create' | 'modify'): EasyApiReturn {
    if (createOrModify === 'create') {
      imgCfg.id = generateUnoccupiedId($s.$state.device.osd.imgList)
    }
    return api.createOrUpdateOsdImgCfg(imgCfg).then((r) => {
      if (r.ok) {
        $s.device.mutations.assignOrCreateOsdImg(imgCfg)
        return true
      } else {
        return false
      }
    })
  }
  static deleteOsd (osd: OsdConfig): EasyApiReturn {
    return api.deleteOsd({ id: osd.id }).then((r) => {
      if (r.ok) {
        showSnackbar(i18n.t('msg.applyDone'))
        $s.device.mutations.deleteOsd(osd)
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static createPreset (preset: Preset): EasyApiReturn {
    const newId = generateUnoccupiedId($s.$state.device.presetList)
    preset.id = newId
    return api.createOrUpdatePreset(preset).then((r) => {
      if (r.ok) {
        $s.device.mutations.assignOrCreatePreset(preset)
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static fetchPresetList (): EasyApiReturn {
    return api.getPresetList().then((r) => {
      if (r.ok) {
        $s.device.mutations.assignPresetList(r.d)
        return true
      } else {
        return false
      }
    })
  }
  static renamePreset (id: number, newName: string, newNote: string): EasyApiReturn {
    const preset = $s.$state.device.presetList.find(v => v.id === id)
    if (!preset) { throw new Error('[To Developer] This should not be happened.') }
    const newObj: Preset = Object.assign({}, preset, { name: newName, note: newNote })
    return api.createOrUpdatePreset(newObj).then(r => {
      handleCommonError(r)
      if (r.ok) {
        $s.device.mutations.assignOrCreatePreset(newObj)
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static deletePreset (preset: Preset): EasyApiReturn {
    return api.deletePreset({ id: preset.id }).then((r) => {
      if (r.ok) {
        showSnackbar(i18n.t('msg.applyDone'))
        $s.device.mutations.deletePreset(preset)
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
  static applyPreset (preset: Preset): EasyApiReturn {
    return api.applyPreset({ id: preset.id }).then((r) => {
      if (r.ok) {
        showSnackbar(i18n.t('msg.applyDone'))
        return true
      } else {
        showSnackbar(i18n.t('msg.applyFailed'))
        return false
      }
    })
  }
}
